import React from 'react';
import styled from 'styled-components';
import Confetti from 'react-confetti';
import { FaTrophy, FaRedo } from 'react-icons/fa';

const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 80vh;
  text-align: center;
  padding: 2rem;
  position: relative;
`;

const ScoreCard = styled.div`
  background-color: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  max-width: 600px;
  width: 100%;
  position: relative;
  z-index: 10;
`;

const ScoreTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #4169E1;
`;

const ScoreValue = styled.div`
  font-size: 4rem;
  font-weight: bold;
  color: #FF9500;
  margin: 1.5rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ScoreMessage = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2rem;
  color: #2C3E50;
`;

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  font-weight: bold;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const RestartButton = styled(ActionButton)`
  background-color: #4169E1;
  color: white;
`;

const Balloon = styled.div`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size * 1.2}px;
  border-radius: 50% 50% 50% 50% / 40% 40% 60% 60%;
  background-color: ${props => props.color};
  animation: float ${props => props.duration}s ease-in-out infinite;
  bottom: -100px;
  left: ${props => props.left}%;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    width: 4px;
    height: ${props => props.size * 0.6}px;
    background-color: #ccc;
    bottom: -${props => props.size * 0.5}px;
    left: 50%;
    transform: translateX(-50%);
  }
  
  @keyframes float {
    0% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-${props => props.floatHeight}px);
    }
    100% {
      transform: translateY(0);
    }
  }
`;

const ResultScreen = ({ score, totalQuestions, onRestart }) => {
  const percentage = (score / totalQuestions) * 100;
  
  let message = '';
  if (percentage >= 90) {
    message = "Άριστα! Είσαι πραγματικός ιστορικός! 🏆";
  } else if (percentage >= 70) {
    message = "Πολύ καλά! Έχεις εξαιρετικές γνώσεις! 🌟";
  } else if (percentage >= 50) {
    message = "Καλή προσπάθεια! Συνέχισε το διάβασμα! 📚";
  } else {
    message = "Χρειάζεσαι λίγη ακόμα μελέτη! 📖";
  }
  
  // Generate balloons based on score
  const balloonCount = Math.max(5, Math.floor(percentage / 10));
  const balloonColors = ['#4169E1', '#FF9500', '#32CD32', '#FF4757', '#FFD700'];
  
  const balloons = Array.from({ length: balloonCount }).map((_, index) => ({
    id: index,
    size: Math.random() * 30 + 40,
    color: balloonColors[index % balloonColors.length],
    left: Math.random() * 90 + 5,
    duration: Math.random() * 5 + 8,
    floatHeight: Math.random() * 200 + 300
  }));

  return (
    <ResultContainer>
      {percentage >= 50 && <Confetti recycle={false} numberOfPieces={200} />}
      
      {balloons.map(balloon => (
        <Balloon 
          key={balloon.id}
          size={balloon.size}
          color={balloon.color}
          left={balloon.left}
          duration={balloon.duration}
          floatHeight={balloon.floatHeight}
        />
      ))}
      
      <ScoreCard>
        <ScoreTitle>Το Σκορ σου</ScoreTitle>
        <ScoreValue>
          <FaTrophy color="#FFD700" size={40} style={{ marginRight: '15px' }} />
          {score}/{totalQuestions}
        </ScoreValue>
        <ScoreMessage>{message}</ScoreMessage>
        
        <ButtonsContainer>
          <RestartButton onClick={onRestart}>
            <FaRedo size={16} />
            Παίξε ξανά
          </RestartButton>
        </ButtonsContainer>
      </ScoreCard>
    </ResultContainer>
  );
};

export default ResultScreen;
